/**
 * Canonical public URL map + home page content for St John Travel & Life.
 *
 * IMPORTANT (SEO): these paths intentionally mirror the legacy reservestj.com
 * URLs one-for-one so years of search ranking are not lost. Do NOT "tidy" a
 * slug here - if a URL changes it must be a deliberate redirect, not a rename.
 *
 * Single source of truth shared by the header, the home page and the footer so
 * the three can never drift apart.
 */

export const ROUTES = {
  // primary funnels
  allToursCharters: '/all-tours-charters',
  bookYourTrip: '/book-your-trip-to-st-john-vi',
  stThomasToursCharters: '/st-thomas-tours-charters',

  // concierge
  eliteConciergeServices: '/elite-concierge-services',
  conciergeServices: '/concierge-services-st-john-vi',

  // bookable charters & excursions
  privateBoatCharters: '/tours-charters-st-john-virgin-islands/private-boat-charters',
  sharedBoatCharters: '/booking-shared-boat-charters-in-st-john-vi',
  kayakExcursions: '/kayak-excursions-st-john-vi',
  sunsetSails: '/sunset-sail-excursions-st-john',
  hikingExcursions: '/booking-hiking-excursions-st-john-vi',
  airportTransfers: '/airport-transfer-services-st-john',
  bviTours: '/booking-bvi-tours-st-john',
  limeOut: '/lime-out-excursions',
  fishingCharters: '/fishing-charters-st-john-vi',
  scubaExcursions: '/scuba-excursions-st-john-vi',
  otherExcursions: '/other-excursions-st-john-vi',
  nightKayak: '/night-kayak',

  // special excursions (charter partners)
  viPlatinumLuxury: '/vi-platinum-luxury-transportation-services-st-john',
  viPlatinumAirport: '/vi-platinum-airport-transfers-services-st-john',
  stormyPirates: '/stormy-pirates-usvi',
  captainJug: '/captain-jug',
  islandBuddy: '/island-buddy-charters',
  saltDaze: '/salt-daze-usvi-st-john',

  // island info (exact legacy paths from the live main menu)
  liveWebcams: '/island-info/st-john-live-webcams',
  transportation: '/island-info/transportation-st-john',
  waterTaxi: '/water-taxi-st-john',
  carBarge: '/st-john-vi-car-barge-information',
  passengerFerry: '/st-john-vi-passenger-ferry-information',
  westinFerry: '/st-john-vi-westin-ferry-information',
  islandTaxi: '/island-taxi-st-john',
  publicTransportation: '/public-transportation-st-john',
  foodDrink: '/island-info/food-drink',
  beachInfo: '/island-info/beach-info-st-john',
  hikingInfo: '/island-info/hiking-in-st-john',
  weather: '/island-info/weather-in-st-john-vi',

  // activities (legacy paths)
  bookableServices: '/#bookable-services',
  discountsFreeStuff: '/activities/discounts-free-stuff-st-john-vi',
  chartersByHost: '/activities/charters-by-host-st-john-vi',

  // concierge (legacy paths)
  contactConcierge: '/concierge-services-st-john-vi/contact-concierge-services-st-john-vi',
  sendATip: '/shop/Send-a-Tip-p491797060',
  sendADeposit: '/shop/Send-a-Deposit-p611729507',

  // contact (legacy path)
  contactUs: '/contacting-us-in-st-john',

  // island directory
  islandDirectory: '/island-directory',
  businessListingPricing: '/business-listing-pricing-packages',
  advertiseWithUsId: '/advertise-with-us-id',
  autoRentals: '/auto-rentals-st-john',
  airlineCustomerService: '/airline-cust-service-st-john',
  foodDrinks: '/food-drinks-st-john',
  villaRentals: '/st-john-villa-rentals',
  islandActivitiesMap: '/island-activities-map',

  // reserve st john
  advertiseWithUs: '/advertise-with-us',
  blog: '/blog',
  contestEntry: '/contest-entry',

  // legacy footer links that live on other reservestj systems (kept external on purpose)
  knowledgebase: 'https://my.reservestj.com/knowledgebase',
  billingPortal: 'https://office.reservestj.com/',
  shopForMen: 'https://reservestj.com/shop/For-Men-c132827121',
  shopForWomen: 'https://reservestj.com/shop/For-Women-c132827121',
  shopForKids: 'https://reservestj.com/shop/For-Kids-c132827122',
  facebookGroup: 'https://www.facebook.com/groups/stjohntravelandlife2.0',

  // in-app (existing app features)
  shop: '/shop',
  activities: '/activities',
  activitiesCompanies: '/activities/companies',
  contact: '/contact',
};

const IMG = '/images/stj';

/** Hero copy - mirrors the legacy home page. */
export const HERO = {
  eyebrowCta: { label: 'FOR MORE INFO', href: ROUTES.bookYourTrip },
  heading: 'ST JOHN THINGS TO DO',
  primaryCta: { label: 'TOURS ~ EXCURSIONS ~ CHARTERS', href: ROUTES.allToursCharters },
  subheading: 'Tours ~ Villas ~ Charters ~ Boats ~ Beaches ~ Restaurants',
};

/** The two "FEATURING" spotlights. */
export const FEATURED = [
  { title: 'NIGHT KAYAK', href: ROUTES.nightKayak, image: `${IMG}/night-kayak.webp` },
  { title: 'BVI TOURS', href: ROUTES.bviTours, image: `${IMG}/bvi-tours-featured.webp` },
];

/**
 * "Charters, Tours & Activities in St John" grid.
 * Order and titles mirror the live site exactly (4 across, 3 rows), and the
 * card title is rendered as an overlay on the photo - not as a caption below.
 */
export const SERVICES = [
  { title: 'Boat Charters Private', href: ROUTES.privateBoatCharters, image: `${IMG}/private-boat-charters.webp`, alt: 'Private Boat Charters St John' },
  { title: 'Boat Charters Shared', href: ROUTES.sharedBoatCharters, image: `${IMG}/shared-boat-charters.webp`, alt: 'Shared Boat Charters St John' },
  { title: "BVI Trips and Ferry's", href: ROUTES.bviTours, image: `${IMG}/bvi-trips-ferry.webp`, alt: 'BVI Trips and Ferries from St John' },
  { title: 'Fishing Charters', href: ROUTES.fishingCharters, image: `${IMG}/fishing-charters.webp`, alt: 'Fishing Charters St John' },
  { title: 'Kayak Excursions', href: ROUTES.kayakExcursions, image: `${IMG}/kayak-excursions.webp`, alt: 'Kayak Excursions St John' },
  { title: 'Land & Hiking Excursions', href: ROUTES.hikingExcursions, image: `${IMG}/hiking-excursions.webp`, alt: 'Hiking Excursions St John' },
  { title: 'Lime Out Excursions', href: ROUTES.limeOut, image: `${IMG}/lime-out.webp`, alt: 'Lime Out Excursions St John' },
  { title: 'Scuba Excursions', href: ROUTES.scubaExcursions, image: `${IMG}/scuba-excursions.webp`, alt: 'Scuba Diving Excursions St John' },
  { title: 'Sunset Sails', href: ROUTES.sunsetSails, image: `${IMG}/sunset-sails.webp`, alt: 'Sunset Sail Excursions St John' },
  { title: "Taxi's, Ferry's and Airport Transfers", href: ROUTES.airportTransfers, image: `${IMG}/airport-transfers.webp`, alt: 'Airport Transfer Services St John' },
  { title: 'Your St John Concierge', href: ROUTES.eliteConciergeServices, image: `${IMG}/elite-concierge.webp`, alt: 'Your Personal Vacation Planner' },
  { title: 'Other Excursions', href: ROUTES.otherExcursions, image: `${IMG}/other-excursions.webp`, alt: 'Other Excursions and Tours St John' },
];

/** Three value props under the villa section. */
export const VALUE_PROPS = [
  {
    title: 'Hand picked Tours',
    icon: `${IMG}/icon-tours.webp`,
    body: 'Our handpicked island tours deliver iconic experiences, with local partners who are the best in each of their fields.',
  },
  {
    title: 'Expert Local Guides',
    icon: `${IMG}/icon-local-map.webp`,
    href: ROUTES.allToursCharters,
    body: 'Let us connect you with a local St. John private guide and arrange a trip just for you and your family.',
  },
  {
    title: 'Free Beach Chairs',
    icon: `${IMG}/icon-beach-chair.webp`,
    body: 'Book direct on our site for your villa, boat, kayak or helicopter tour. When you book direct, you earn FREE beach chair rentals.',
  },
];

/**
 * Header mega-menu - an EXACT mirror of the live reservestj.com main menu
 * (`ul.sp-megamenu-parent`). Top level, in order:
 *   ISLAND INFO | ACTIVITIES | GIFT SHOP | CONCIERGE | CONTACT US
 * Do not add, rename or reorder these without the owner's say-so.
 */
export const MAIN_MENU = [
  {
    label: 'ISLAND INFO',
    items: [
      { label: 'Island Live Cams', href: ROUTES.liveWebcams, flag: 'webcams_enabled' },
      { label: 'Transportation', href: ROUTES.transportation, flag: 'transportation_enabled' },
      { label: 'Food & Drink', href: ROUTES.foodDrink, flag: 'food_drink_enabled' },
      { label: 'Island Beach Info', href: ROUTES.beachInfo, flag: 'beaches_enabled' },
      { label: 'Hiking in St John', href: ROUTES.hikingInfo, flag: 'hiking_enabled' },
      { label: 'Island Weather', href: ROUTES.weather, flag: 'weather_enabled' },
      { label: 'Westin Rentals', href: 'https://stjohncondos.com/', external: true },
    ],
  },
  {
    label: 'ACTIVITIES',
    href: ROUTES.bookableServices,
    items: [
      { label: 'All Activities', href: ROUTES.bookableServices },
      { label: 'Discounts & Free Stuff', href: ROUTES.discountsFreeStuff },
      { label: 'By Charter Company', href: ROUTES.chartersByHost },
    ],
  },
  { label: 'GIFT SHOP', href: ROUTES.shop },
  {
    label: 'CONCIERGE',
    href: ROUTES.conciergeServices,
    items: [
      { label: 'Feeling Tipsy? Send a Tip', href: ROUTES.sendATip },
      { label: 'Send a Deposit', href: ROUTES.sendADeposit },
      { label: 'Contact Us', href: ROUTES.contactConcierge },
    ],
  },
  { label: 'CONTACT US', href: ROUTES.contactUs },
];

/**
 * Contact page content, mirroring reservestj.com/contacting-us-in-st-john exactly:
 * the two published phone lines, the mailing address, the Facebook group, the Calendly link
 * and the Reason For Contact choices from the live form. The email address comes from
 * Developer Settings at runtime, so it is not repeated here.
 */
export const CONTACT_PAGE = {
  heading: 'Contacting St John Travel & Life',
  subheading: 'St John, Virgin Islands',
  tagline: 'Local Knowledge For Your Best Vacation',
  formTitle: 'Your Personal Information',
  reasons: [
    'Free Concierge Service',
    'Question About A Listing',
    'Info About An Activity',
    'Advertising',
    'Other',
  ],
  consent:
    'By providing your email and phone number, you agree to our Privacy Policy and terms of use to '
    + 'receive calls or text messages from Reserve St John, including emails and calls regarding your '
    + 'free quote. Your consent to receive marketing calls is not a condition of purchase. Message and '
    + 'data rates may apply.',
  mailingAddress: 'P.O. Box 1797 St John VI 00831',
  phones: [
    { number: '(340) 513-7080', tel: 'tel:13405137080', label: 'Txt or Call Help Line' },
    { number: '(340) 998-3685', tel: 'tel:13409983685', label: 'Kris' },
  ],
  facebookGroup: 'https://www.facebook.com/groups/269556383563172',
  /**
   * Where "Schedule Complimentary Call" goes. An in-app booking page (/book/<slug>) keeps the
   * visitor on the site and the meeting in this system; set a full https:// URL here instead if a
   * site ever needs an external scheduler.
   */
  scheduleCall: '/book/kris-dougherty',
};

/** Footer link groups (EXCURSION COMPANIES is generated from the database). */
export const FOOTER_MENUS = {
  conciergeServices: [
    { label: 'Elite Concierge Services', href: ROUTES.conciergeServices },
    { label: 'All Tours & Charters', href: ROUTES.allToursCharters },
    { label: 'St Thomas Tours & Charters', href: ROUTES.stThomasToursCharters },
    { label: 'Elite Service Packages', href: ROUTES.eliteConciergeServices },
    { label: 'Book Your Trip', href: ROUTES.bookYourTrip },
  ],
  specialExcursions: [
    { label: 'VI Platinum Transportation', href: ROUTES.viPlatinumLuxury },
    { label: 'BVI Tours', href: ROUTES.bviTours },
    { label: 'Private Boat Charters', href: ROUTES.privateBoatCharters },
    { label: 'Shared Boat Charters', href: ROUTES.sharedBoatCharters },
    { label: 'Stormy Pirates', href: ROUTES.stormyPirates },
    { label: 'Night Kayak', href: ROUTES.nightKayak },
    { label: 'Captain Jug', href: ROUTES.captainJug },
    { label: 'Island Buddy', href: ROUTES.islandBuddy },
    { label: 'Salt Daze USVI', href: ROUTES.saltDaze },
  ],
  islandDirectory: [
    { label: 'Island Directory', href: ROUTES.islandDirectory },
    { label: 'Island Activities Map', href: ROUTES.islandActivitiesMap },
    { label: 'Business Listing Pricing/Packages', href: ROUTES.businessListingPricing },
    { label: 'Advertise With Us', href: ROUTES.advertiseWithUsId },
    { label: 'Auto Rental', href: ROUTES.autoRentals },
    { label: 'Airline Customer Service', href: ROUTES.airlineCustomerService },
    { label: 'Food & Drinks', href: ROUTES.foodDrinks },
    { label: 'Villa Rentals', href: ROUTES.villaRentals },
  ],
  reserveStJohn: [
    { label: 'Knowledgebase', href: ROUTES.knowledgebase },
    { label: 'Advertise With Us', href: ROUTES.advertiseWithUs },
    { label: 'Billing Portal', href: ROUTES.billingPortal },
    { label: 'Blog', href: ROUTES.blog },
    { label: "FAQ's", href: ROUTES.knowledgebase },
    { label: 'Contest Entry', href: ROUTES.contestEntry },
    { label: 'Island Live Cams', href: ROUTES.liveWebcams },
    { label: 'Contact Us', href: ROUTES.contactUs },
  ],
  onlineShop: [
    { label: 'For Men', href: ROUTES.shopForMen },
    { label: 'For Women', href: ROUTES.shopForWomen },
    { label: 'For Kids', href: ROUTES.shopForKids },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms-conditions' },
    { label: 'Accessibility', href: '/accessibility' },
    { label: 'Shipping & Returns', href: '/shipping-returns' },
  ],
};
